/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";

import {
    Box,
    Alert,
    Button,
    CardContent,
    Divider,
    Stack,
    Switch,
    TextField,
    Typography,
    IconButton,
    Tooltip,
    CircularProgress,
} from "@mui/material";

import Checkbox from "@mui/material/Checkbox";
import Card from "@mui/material/Card";
import { Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";

import HistoryEduIcon from "@mui/icons-material/HistoryEdu";

import {
    ChecklistItem,
    Effect,
    FullAssignedTechnicianEntry,
    FullTechnicianEntry,
    Nullable,
    Setter,
    TechnicianEntry,
    TechnicianEntryEgressInventory,
} from "../../utils/Interfaces";

import AttachmentManager from "../../components/general/AttachmentManager";
import {
    getInventoriesForTechnicianEntry,
    setTechnicianEntryEgressForm,
    submitTechnicianEntryV2,
} from "./technicianWorkOrderFunctions";
import MobileTechnicianChecklistItem from "../../components/form-controls/MobileTechnicianChecklistItem";
import MobileParagraphEdit from "../../components/form-controls/MobileParagraphEdit";
import MobileTimeSelector from "../../components/form-controls/MobileTimeSelector";
import { modifyField } from "../../components/form-controls/formControlsFunctions";
import { useWindowDimensions } from "../../utils/Hooks";
import { useAppDispatch } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import InventoryEgressModal from "./InventoryEgressModal";
import { Add, Check, Delete, LocationOn, Person, SpeakerNotes } from "@mui/icons-material";
import { NumericFormat } from "react-number-format";
import InfoModal from "../../components/modal/InfoModal";
import { ModelName } from "../../utils/Requests";

export interface TechnicianEntryEgressEntryProps {
    technicianEntry: TechnicianEntry;
    technicianEntryEgress: TechnicianEntryEgressInventory;
    divider: boolean;
    onTechnicianEgressAdded: Effect;
}

const TechnicianEntryEgressEntry = (props: TechnicianEntryEgressEntryProps) => {
    const { technicianEntry, technicianEntryEgress, divider, onTechnicianEgressAdded } = props;

    const dispatch = useAppDispatch();

    const [amount, setAmount] = useState<number>(technicianEntryEgress.amount);
    const [updating, setUpdating] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);

    const available: number = (technicianEntryEgress as any).available;
    const maxCountAvailable: number = available + technicianEntryEgress.amount;

    const [viewNotesModalOpen, setViewNotesModalOpen] = useState<boolean>(false);

    useEffect(() => {
        setAmount(technicianEntryEgress.amount);
        setUpdating(false);
        setDeleting(false);
    }, [technicianEntryEgress]);

    return (
        <Box>
            <InfoModal
                title="Part/Material Notes"
                description={technicianEntryEgress.notes ?? "--"}
                open={viewNotesModalOpen}
                handleClose={() => setViewNotesModalOpen(false)}
            />

            <Stack direction="row" justifyContent="flex-start">
                <Typography variant="h6">{technicianEntryEgress.inventory.material.name}</Typography>
                {technicianEntryEgress.notes !== null && technicianEntryEgress.notes !== "" && (
                    <Tooltip title="View Part/Material Notes">
                        <IconButton sx={{ mt: -0.25, ml: 2 }} onClick={() => setViewNotesModalOpen(true)}>
                            <SpeakerNotes fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                <Stack direction="column" justifyContent="flex-start">
                    <Stack direction="row" justifyContent="flex-start">
                        <LocationOn fontSize="small" />
                        <Typography variant="subtitle2" sx={{ ml: 1 }}>
                            {technicianEntryEgress.inventory.storage_location.name}
                        </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="flex-start">
                        <Person fontSize="small" />
                        <Typography variant="subtitle2" sx={{ ml: 1 }}>
                            {technicianEntryEgress.inventory.storage_location.responsible_user?.first_name ?? "--"}
                            &nbsp;
                            {technicianEntryEgress.inventory.storage_location.responsible_user?.last_name ?? ""}
                        </Typography>
                    </Stack>
                </Stack>

                <Stack direction="row" justifyContent="flex-end">
                    <Stack direction="row" justifyContent="flex-start">
                        {!updating && !deleting && (
                            <NumericFormat
                                size="small"
                                value={amount}
                                customInput={TextField}
                                style={{ marginLeft: 0, marginTop: 0, width: 100 }}
                                label="Update Amt."
                                thousandSeparator=","
                                decimalScale={0}
                                allowNegative={false}
                                allowLeadingZeros={false}
                                onValueChange={(values) => {
                                    setAmount(parseInt(values.value));
                                }}
                            />
                        )}

                        {(updating || deleting) && (
                            <Stack direction="column" justifyContent="center" sx={{ mr: 1 }}>
                                <CircularProgress size={16} />
                            </Stack>
                        )}

                        {!updating && !deleting && (
                            <Box>
                                <Tooltip title="Update Material Amount Used">
                                    <IconButton
                                        disabled={amount === technicianEntryEgress.amount || amount > maxCountAvailable}
                                        onClick={() => {
                                            setUpdating(true);
                                            setTechnicianEntryEgressForm(
                                                technicianEntry.id,
                                                technicianEntryEgress.inventory.id,
                                                amount,
                                                technicianEntryEgress.notes,
                                                dispatch,
                                                () => {
                                                    onTechnicianEgressAdded();
                                                    setUpdating(false);
                                                }
                                            );
                                        }}
                                    >
                                        <Check />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Remove Material Entry">
                                    <IconButton
                                        onClick={() => {
                                            setDeleting(true);
                                            setTechnicianEntryEgressForm(
                                                technicianEntry.id,
                                                technicianEntryEgress.inventory.id,
                                                0,
                                                technicianEntryEgress.notes,
                                                dispatch,
                                                () => {
                                                    onTechnicianEgressAdded();
                                                    setDeleting(false);
                                                }
                                            );
                                        }}
                                    >
                                        <Delete />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </Stack>
                </Stack>
            </Stack>

            {divider && <Divider sx={{ mb: 2, mt: 2 }} />}

            {!divider && <Box sx={{ mb: 3 }} />}
        </Box>
    );
};

export interface TechnicianDashboardEntryProps {
    technicianEntry: FullAssignedTechnicianEntry;
    refreshEntry: Setter<Effect>;
}

const TechnicianDashboardEntryV2 = (props: TechnicianDashboardEntryProps) => {
    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const { width } = useWindowDimensions();
    let mobile: boolean = width <= 600;

    const { technicianEntry, refreshEntry } = props;

    const [noMaterialsUsed, setNoMaterialsUsed] = useState<boolean>(false);

    const [egressModalOpen, setEgressModalOpen] = useState<boolean>(false);

    const [technicianEntryEgresses, setTechnicianEntryEgresses] = useState<TechnicianEntryEgressInventory[]>([]);

    const nav = useNavigate();

    useEffect(() => {
        getInventoriesForTechnicianEntry(technicianEntry.id, dispatch, setTechnicianEntryEgresses);
    }, [technicianEntry]);

    const {
        travel_start_time,
        time_arrived_on_site,
        time_site_left,
        work_performed,
        other_notes,
        parts_needed_to_return,
        service_channel_ticket_no,
        return_trip_needed,
        signature,
        tasks,
        materials_write_in,
    } = technicianEntry;

    let travelStartTimeComp: Nullable<string> = travel_start_time;
    let timeArrivedOnSiteComp: Nullable<string> = time_arrived_on_site;
    let timeSiteLeftComp: Nullable<string> = time_site_left;

    const [returnTripNeeded, setReturnTripNeeded] = useState<boolean>(return_trip_needed);

    function getFormErrors(): string[] {
        let formErrors: string[] = [];

        if (travelStartTimeComp === null || travelStartTimeComp === undefined || travelStartTimeComp === "") {
            formErrors.push("Please enter the Travel Start Time.");
        }

        if (timeArrivedOnSiteComp === null || timeArrivedOnSiteComp === undefined || timeArrivedOnSiteComp === "") {
            formErrors.push("Please enter the Time Arrived on Site.");
        }

        if (timeSiteLeftComp === null || timeSiteLeftComp === undefined || timeSiteLeftComp === "") {
            formErrors.push("Please enter the Time Left Site.");
        }

        if (work_performed === null || work_performed === undefined || work_performed === "") {
            formErrors.push("Please enter the Work Performed.");
        }

        if (parts_needed_to_return === null || parts_needed_to_return === undefined || parts_needed_to_return === "") {
            formErrors.push("Please enter any Parts Needed to Return OR Select N/A.");
        }

        if (
            technicianEntryEgresses.length === 0 &&
            (materials_write_in === "" || materials_write_in === null) &&
            !noMaterialsUsed
        ) {
            formErrors.push("Please add parts/materials used OR confirm that none were used.");
        }

        tasks.map((task: ChecklistItem) => {
            if (task.status === 3) {
                formErrors.push(`Task "${task.label}" has not been marked.`);
            }
            return null;
        });

        return formErrors;
    }

    function canSubmit(): boolean {
        return getFormErrors().length === 0;
    }

    return (
        <Card style={{ marginTop: 10 }}>
            <CardContent style={{ margin: 0 }}>
                <Box>
                    <InventoryEgressModal
                        open={egressModalOpen}
                        handleClose={() => setEgressModalOpen(false)}
                        onSubmitEgressRequest={(
                            inventoryId: number,
                            amount: number,
                            notes: Nullable<string>,
                            modalCallback: Effect
                        ) => {
                            setTechnicianEntryEgressForm(technicianEntry.id, inventoryId, amount, notes, dispatch, modalCallback);
                        }}
                        onTechnicianEgressAdded={() => {
                            getInventoriesForTechnicianEntry(
                                technicianEntry.id,
                                dispatch,
                                (teei: TechnicianEntryEgressInventory[]) => {
                                    setTechnicianEntryEgresses([]);
                                    setTechnicianEntryEgresses(teei);
                                }
                            );
                        }}
                        existingTechnicianEntryEgresses={technicianEntryEgresses}
                    />

                    <Typography variant="h5" textAlign="center" sx={{ mb: 2 }}>
                        Trip #{technicianEntry.trip_number}
                    </Typography>

                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                        Time Entries
                    </Typography>

                    <Box>
                        <MobileTimeSelector<TechnicianEntry>
                            field={travelStartTimeComp}
                            updatePath={`/api/tech-entries/update/${technicianEntry.id}`}
                            updateKey="travel_start_time"
                            label="Travel Start Time"
                            onUpdate={() => refreshEntry(() => {})}
                            readonly={false}
                        />

                        <MobileTimeSelector<TechnicianEntry>
                            field={timeArrivedOnSiteComp}
                            updatePath={`/api/tech-entries/update/${technicianEntry.id}`}
                            updateKey="time_arrived_on_site"
                            label="Time Arrived on Site"
                            onUpdate={() => refreshEntry(() => {})}
                            readonly={false}
                        />

                        <MobileTimeSelector<TechnicianEntry>
                            field={timeSiteLeftComp}
                            updatePath={`/api/tech-entries/update/${technicianEntry.id}`}
                            updateKey="time_site_left"
                            label="Time Left Site"
                            onUpdate={() => refreshEntry(() => {})}
                            readonly={false}
                        />
                    </Box>

                    <Divider sx={{ mt: 2, mb: 2 }} />

                    <Box>
                        <MobileParagraphEdit<TechnicianEntry>
                            field={work_performed}
                            updatePath={`/api/tech-entries/update/${technicianEntry.id}`}
                            updateKey="work_performed"
                            label="Work Performed"
                            onUpdated={refreshEntry}
                            readonly={false}
                            showNA={false}
                        />

                        <Divider sx={{ mt: 3, mb: 3 }}>
                            <Typography variant="h5">Parts/Materials Used</Typography>
                        </Divider>

                        <Stack direction="row" justifyContent="flex-start" sx={{ mb: 2 }}>
                            <Stack direction="column" justifyContent="flex-start">
                                <Switch
                                    id="no_materials_used"
                                    name="no_materials_used"
                                    onChange={() => setNoMaterialsUsed(!noMaterialsUsed)}
                                    checked={noMaterialsUsed}
                                    disabled={
                                        technicianEntryEgresses.length !== 0 ||
                                        (materials_write_in !== "" && materials_write_in !== null) ||
                                        technicianEntry.submitted
                                    }
                                />
                            </Stack>
                            <Typography sx={{ ml: 2, mt: 1 }}>No Parts/Materials Used</Typography>
                        </Stack>

                        {!noMaterialsUsed && (
                            <>
                                {technicianEntryEgresses.map(
                                    (technicianEntryEgress: TechnicianEntryEgressInventory, index: number) => {
                                        return (
                                            <TechnicianEntryEgressEntry
                                                key={index}
                                                technicianEntry={technicianEntry}
                                                technicianEntryEgress={technicianEntryEgress}
                                                divider={index < technicianEntryEgresses.length - 1}
                                                onTechnicianEgressAdded={() => {
                                                    getInventoriesForTechnicianEntry(
                                                        technicianEntry.id,
                                                        dispatch,
                                                        setTechnicianEntryEgresses
                                                    );
                                                }}
                                            />
                                        );
                                    }
                                )}

                                <Button onClick={() => setEgressModalOpen(true)} variant="contained" size="small">
                                    <Add fontSize="small" />
                                    &nbsp; Add Materials from Inventory
                                </Button>

                                <Box sx={{ mb: 5 }} />

                                <MobileParagraphEdit<FullTechnicianEntry>
                                    field={materials_write_in}
                                    updatePath={`/api/tech-entries/update/${technicianEntry.id}`}
                                    updateKey="materials_write_in"
                                    label="Manually Enter Parts/Materials"
                                    onUpdated={refreshEntry}
                                    readonly={false}
                                    lines={4}
                                    showNA={false}
                                />
                            </>
                        )}

                        <Divider sx={{ mt: 3, mb: 3 }}>
                            <Typography variant="h5">Additional Details</Typography>
                        </Divider>

                        <MobileParagraphEdit<TechnicianEntry>
                            field={service_channel_ticket_no}
                            updatePath={`/api/tech-entries/update/${technicianEntry.id}`}
                            updateKey="service_channel_ticket_no"
                            label="Service Channel Ticket #"
                            onUpdated={refreshEntry}
                            readonly={false}
                            lines={1}
                        />

                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mt: 2, mb: 1, ml: 1, mr: 1 }}
                        >
                            <Typography variant="h6">Return Trip Needed</Typography>

                            <Checkbox
                                id="return_trip_needed"
                                name="return_trip_needed"
                                onClick={() => {
                                    let newValue = !returnTripNeeded;
                                    setReturnTripNeeded(newValue);
                                    modifyField<TechnicianEntry>(
                                        `/api/tech-entries/update/${technicianEntry.id}`,
                                        "return_trip_needed",
                                        newValue,
                                        dispatch,
                                        () => refreshEntry(() => {})
                                    );
                                }}
                                checked={returnTripNeeded}
                            />
                        </Grid>

                        <Box sx={{ mb: 2 }} />

                        <MobileParagraphEdit<TechnicianEntry>
                            field={parts_needed_to_return}
                            updatePath={`/api/tech-entries/update/${technicianEntry.id}`}
                            updateKey="parts_needed_to_return"
                            label="Parts Needed to Return"
                            onUpdated={refreshEntry}
                            readonly={false}
                        />

                        <Box marginBottom="10px" />

                        <MobileParagraphEdit<TechnicianEntry>
                            field={other_notes}
                            updatePath={`/api/tech-entries/update/${technicianEntry.id}`}
                            updateKey="other_notes"
                            label="Other Notes"
                            onUpdated={refreshEntry}
                            readonly={false}
                        />

                        <Divider sx={{ mt: 3, mb: 3 }}>
                            <Typography variant="h5">Technician Checklist</Typography>
                        </Divider>

                        <Box style={{ marginBottom: "20px" }} />

                        {tasks.map((checklistItem: ChecklistItem, index: number) => {
                            return (
                                <MobileTechnicianChecklistItem
                                    key={index}
                                    checklistItem={checklistItem}
                                    readonly={false}
                                    onUpdatedChecklistItem={refreshEntry}
                                />
                            );
                        })}
                    </Box>

                    <MobileParagraphEdit<TechnicianEntry>
                        field={signature}
                        updatePath={`/api/tech-entries/update/${technicianEntry.id}`}
                        updateKey="signature"
                        label="Manager/AC Signature"
                        onUpdated={refreshEntry}
                        lines={1}
                        readonly={false}
                        editIcon={<HistoryEduIcon />}
                    />

                    <Divider sx={{ mt: 3, mb: 3 }}>
                        <Typography variant="h5">Images &amp; Attachments</Typography>
                    </Divider>

                    {technicianEntry && (
                        <AttachmentManager
                            model={ModelName.TechnicianEntry}
                            idKey={technicianEntry.id}
                            editing={true}
                            readOnly={false}
                        />
                    )}

                    <Divider sx={{ mt: 2 }}></Divider>

                    {!canSubmit() && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography fontWeight="bold">
                                The following fields need to be corrected before submitting.
                            </Typography>

                            <Box sx={{ mb: 2 }} />

                            {getFormErrors().map((formError: string, index: number) => {
                                return (
                                    <Typography key={index} sx={{ mb: 1 }} variant={mobile ? "subtitle2" : "subtitle1"}>
                                        {`${index + 1}. ${formError}`}
                                    </Typography>
                                );
                            })}
                        </Alert>
                    )}

                    {!technicianEntry.submitted && (
                        <Button
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                            disabled={!canSubmit()}
                            onClick={() =>
                                submitTechnicianEntryV2(
                                    technicianEntry.id,
                                    dispatch,
                                    () => nav("/tech-dash"),
                                    () => {}
                                )
                            }
                        >
                            Submit
                        </Button>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default TechnicianDashboardEntryV2;
